import { Activity, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material'
import { Link, useNavigate, useParams } from 'react-router-dom'

import DetailPageTemplate from '../../templates/DetailPageTemplate'
import PermissionMatrix from './PermissionMatrix'
import fetchWithAuth from '../../utilities/fetchWithAuth'

const RolePage = () => {
  const [role, setRole] = useState(null)
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const { uuid } = useParams()
  const navigate = useNavigate()

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await fetchWithAuth(`roles/${uuid}`)
        setRole(data.data)
      } catch (error) {
        console.error('Error fetching role:', error)
      }
    }

    fetchData()
  }, [uuid])

  const handleCloseDialog = () => {
    setOpenDeleteDialog(false)
  }

  const handleDelete = async () => {
    try {
      await fetchWithAuth(`roles/${uuid}`, { method: 'DELETE' })
      navigate('/roles')
    } catch (error) {
      console.error('Error deleting role:', error)
    }
  }

  if (!role) {
    return <DetailPageTemplate isLoading={true} />
  }

  const detailsSection = {
    title: 'Role Details',
    content: (
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={3}>
            <Box display="flex" alignItems="flex-start" gap={2}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ width: 100 }}
              >
                Name
              </Typography>
              <Typography>{role.name}</Typography>
            </Box>
            <Box display="flex" alignItems="flex-start" gap={2}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ width: 100 }}
              >
                Description
              </Typography>
              <Typography>{role.description || 'No description'}</Typography>
            </Box>
            <Box display="flex" alignItems="flex-start" gap={2}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ width: 100 }}
              >
                Type
              </Typography>
              <Typography component="span">
                {role.isSystem ? (
                  <Chip label="System Role" color="primary" size="small" />
                ) : (
                  'Custom Role'
                )}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    ),
  }

  const permissionsSection = {
    title: 'Permissions',
    content: <PermissionMatrix permissions={role?.permissions ?? []} />,
  }

  return (
    <>
      <DetailPageTemplate
        title={role.name}
        breadcrumbs={['roles', role.name]}
        actions={
          <Activity mode={!role.isSystem ? 'visible' : 'hidden'}>
            <Stack direction="row" spacing={2}>
              <Button
                component={Link}
                to={`/roles/${uuid}/edit`}
                variant="contained"
                color="primary"
              >
                Edit
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={() => setOpenDeleteDialog(true)}
              >
                Delete Role
              </Button>
            </Stack>
          </Activity>
        }
        sections={[detailsSection, permissionsSection]}
      />

      <Dialog open={openDeleteDialog} onClose={handleCloseDialog}>
        <DialogTitle>Delete Role</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {role.name}? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default RolePage
